import React, { useState, useEffect } from "react";
import { ApiKeyOrGetter } from "../../services/GeneralService";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "../ui/table";
import { Skeleton } from "../ui/skeleton";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import update from "immutability-helper";
import { TestRunService } from "../../services/TestRunService";
import {
  TestRunRequest,
  ChatCompletionRequest,
  ChatMessage,
  ToolCall,
} from "../../model_gen/evaluation";
import { DiffIcon, GripVerticalIcon, AlertCircleIcon } from "lucide-react";
import { RequestViewButton } from "../shared/button/Buttons";
import { cn } from "../../lib/utils";

// Custom Score Tooltip Component
interface ScoreTooltipProps {
  children: React.ReactNode;
  score: number;
  results: Array<{
    criteria: string;
    score_value: number;
    description?: string;
  }>;
}

const ScoreTooltip: React.FC<ScoreTooltipProps> = ({ children, score, results }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ left: "50%", transform: "translateX(-50%)" });
  const tooltipRef = React.useRef<HTMLDivElement>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);

  const getScoreLabel = (score: number) => {
    if (score >= 90) return { label: "EXCELLENT", color: "#52c41a" };
    if (score >= 75) return { label: "GREAT", color: "#b7eb8f" };
    if (score >= 60) return { label: "GOOD", color: "#d9d9d9" };
    if (score >= 40) return { label: "FAIR", color: "#ffa39e" };
    return { label: "POOR", color: "#f5222d" };
  };

  const scoreLabel = getScoreLabel(score);
  const sortedResults = [...results].sort((a, b) => a.criteria.localeCompare(b.criteria));

  const updatePosition = () => {
    if (!containerRef.current || !tooltipRef.current) return;

    const containerRect = containerRef.current.getBoundingClientRect();
    const tooltipWidth = 384; // w-96 = 384px
    const viewportWidth = window.innerWidth;
    const margin = 16; // Safety margin from screen edges

    // Calculate the ideal center position
    const idealLeft = containerRect.left + containerRect.width / 2;
    
    // Calculate how much space we need on each side
    const halfTooltipWidth = tooltipWidth / 2;
    
    // Check if tooltip would overflow on the left
    if (idealLeft - halfTooltipWidth < margin) {
      // Position tooltip flush with left margin
      const leftOffset = margin - (idealLeft - halfTooltipWidth);
      setPosition({
        left: "50%",
        transform: `translateX(calc(-50% + ${leftOffset}px))`
      });
    }
    // Check if tooltip would overflow on the right
    else if (idealLeft + halfTooltipWidth > viewportWidth - margin) {
      // Position tooltip flush with right margin
      const rightOffset = (idealLeft + halfTooltipWidth) - (viewportWidth - margin);
      setPosition({
        left: "50%",
        transform: `translateX(calc(-50% - ${rightOffset}px))`
      });
    }
    // Tooltip fits perfectly centered
    else {
      setPosition({
        left: "50%",
        transform: "translateX(-50%)"
      });
    }
  };

  const handleMouseEnter = () => {
    setIsVisible(true);
    // Update position after the tooltip is visible
    setTimeout(updatePosition, 0);
  };

  const handleMouseLeave = () => {
    setIsVisible(false);
  };

  // Update position on window resize
  React.useEffect(() => {
    if (!isVisible) return;

    const handleResize = () => {
      updatePosition();
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isVisible]);

  return (
    <div 
      ref={containerRef}
      className="relative"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {children}
      {isVisible && (
        <div 
          className="absolute top-full mt-2 z-[9999] pointer-events-none"
          style={{
            left: position.left,
            transform: position.transform
          }}
        >
          <div 
            ref={tooltipRef}
            className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl p-4 w-96 max-w-md"
          >
            {/* Tooltip arrow */}
            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-white dark:border-b-gray-800"></div>
            
            {/* Header */}
            <div className="flex items-center justify-between mb-3 pb-2 border-b border-gray-100 dark:border-gray-700">
              <div className="flex items-center gap-2">
                <div 
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: scoreLabel.color }}
                />
                <span className="font-semibold text-foreground text-sm">
                  Overall Score
                </span>
              </div>
              <div className="flex items-center gap-1">
                <span className="text-xl font-bold text-foreground">{score}%</span>
                <span 
                  className="text-xs font-medium px-2 py-1 rounded-full"
                  style={{ 
                    backgroundColor: `${scoreLabel.color}20`,
                    color: scoreLabel.color 
                  }}
                >
                  {scoreLabel.label}
                </span>
              </div>
            </div>

            {/* Criteria Breakdown */}
            <div className="space-y-2">
              <div className="text-xs font-medium text-muted-foreground mb-2">
                CRITERIA BREAKDOWN
              </div>
              {sortedResults.map((result, index) => (
                <div key={index} className="space-y-1">
                  {/* Use a grid layout to ensure proper spacing */}
                  <div className="grid grid-cols-[1fr,auto] gap-3 items-start">
                    <div className="min-w-0"> {/* min-w-0 allows text to shrink */}
                      <span className="text-sm font-medium text-foreground break-words">
                        {result.criteria}
                      </span>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {/* Star rating visual - flex-shrink-0 prevents compression */}
                      <div className="flex gap-0.5">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <div
                            key={star}
                            className={cn(
                              "w-3 h-3 rounded-sm flex-shrink-0",
                              star <= result.score_value
                                ? "bg-maitai-green"
                                : "bg-gray-200 dark:bg-gray-600"
                            )}
                          />
                        ))}
                      </div>
                      <span className="text-xs font-mono text-muted-foreground ml-1 whitespace-nowrap">
                        {result.score_value}/5
                      </span>
                    </div>
                  </div>
                  {result.description && (
                    <p className="text-xs text-muted-foreground leading-relaxed pl-1 break-words">
                      {result.description}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Custom Responses Tooltip Component  
interface ResponsesTooltipProps {
  children: React.ReactNode;
  originalRequest: ChatCompletionRequest;
  testRunResponses: Array<{
    testRunId: string;
    response: string;
    model?: string;
  }>;
}

const ResponsesTooltip: React.FC<ResponsesTooltipProps> = ({ children, originalRequest, testRunResponses }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ left: "50%", transform: "translateX(-50%)" });
  const tooltipRef = React.useRef<HTMLDivElement>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);

  const renderToolCalls = (toolCalls: ToolCall[]) => {
    return toolCalls.map((toolCall: ToolCall) => (
      <div key={toolCall.id} className="text-xs text-muted-foreground mt-2 p-2 bg-gray-50 dark:bg-gray-800 rounded border-l-2 border-maitai-green">
        <div className="font-medium text-foreground">{toolCall.function.name}</div>
        <div className="font-mono text-xs">{toolCall.function.arguments}</div>
      </div>
    ));
  };

  // Get original response - match exactly how RequestView.tsx gets it
  let originalResponse = "No response";

  // First check for _maitai_test_request_response in metadata (this is the edited/corrected response)
  const testResponse = originalRequest.metadata?._maitai_test_request_response;
  if (testResponse !== undefined && testResponse !== null) {
    originalResponse = testResponse as string;
  }
  // If no test response, get the last assistant message content
  else if (originalRequest?.params?.messages) {
    const messages = originalRequest?.params?.messages;
    // Find the last assistant message
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === "assistant" && messages[i].content) {
        originalResponse = messages[i].content;
        break;
      }
    }
  }

  const updatePosition = () => {
    if (!containerRef.current || !tooltipRef.current) return;

    const containerRect = containerRef.current.getBoundingClientRect();
    const tooltipWidth = 900; // Our tooltip width
    const viewportWidth = window.innerWidth;
    const margin = 16; // Safety margin from screen edges

    // Calculate the ideal center position
    const idealLeft = containerRect.left + containerRect.width / 2;
    
    // Calculate how much space we need on each side
    const halfTooltipWidth = tooltipWidth / 2;
    
    // Check if tooltip would overflow on the left
    if (idealLeft - halfTooltipWidth < margin) {
      // Position tooltip flush with left margin
      const leftOffset = margin - (idealLeft - halfTooltipWidth);
      setPosition({
        left: "50%",
        transform: `translateX(calc(-50% + ${leftOffset}px))`
      });
    }
    // Check if tooltip would overflow on the right
    else if (idealLeft + halfTooltipWidth > viewportWidth - margin) {
      // Position tooltip flush with right margin
      const rightOffset = (idealLeft + halfTooltipWidth) - (viewportWidth - margin);
      setPosition({
        left: "50%",
        transform: `translateX(calc(-50% - ${rightOffset}px))`
      });
    }
    // Tooltip fits perfectly centered
    else {
      setPosition({
        left: "50%",
        transform: "translateX(-50%)"
      });
    }
  };

  const handleMouseEnter = () => {
    setIsVisible(true);
    // Update position after the tooltip is visible
    setTimeout(updatePosition, 0);
  };

  const handleMouseLeave = () => {
    setIsVisible(false);
  };

  // Update position on window resize
  React.useEffect(() => {
    if (!isVisible) return;

    const handleResize = () => {
      updatePosition();
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isVisible]);

  return (
    <div 
      ref={containerRef}
      className="relative"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {children}
      {isVisible && (
        <div 
          className="absolute top-full mt-2 z-[9999] pointer-events-none"
          style={{
            left: position.left,
            transform: position.transform
          }}
        >
          <div 
            ref={tooltipRef}
            className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl p-6 w-[900px] max-w-[900px]"
          >
            {/* Tooltip arrow */}
            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-white dark:border-b-gray-800"></div>
            
            {/* Header */}
            <div className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-100 dark:border-gray-700">
              <DiffIcon className="w-4 h-4 text-maitai-green" />
              <span className="font-semibold text-foreground">Response Comparison</span>
            </div>

            {/* Multi-column comparison */}
            <div 
              className="grid gap-6"
              style={{
                gridTemplateColumns: testRunResponses.length > 0
                  ? `repeat(${testRunResponses.length + 1}, 1fr)`
                  : "1fr 1fr"
              }}
            >
              {/* Original Response */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                  <span className="font-medium text-foreground text-sm">
                    Original Response
                    {originalRequest.metadata?._maitai_test_request_response && (
                      <span className="text-maitai-green ml-1">*</span>
                    )}
                  </span>
                </div>
                <div className="max-h-[300px] overflow-y-auto bg-gray-50 dark:bg-gray-900 rounded border p-3">
                  <div className="whitespace-pre-wrap break-words text-sm text-foreground leading-relaxed">
                    {originalResponse}
                  </div>
                </div>
              </div>

              {/* Test Run Responses */}
              {testRunResponses.map(({ testRunId, response, model }) => (
                <div key={testRunId}>
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-2 h-2 bg-maitai-green rounded-full"></div>
                    <div className="flex flex-col">
                      <span className="font-medium text-foreground text-sm">Run {testRunId}</span>
                      {model && (
                        <span className="text-xs text-muted-foreground">{model}</span>
                      )}
                    </div>
                  </div>
                  <div className="max-h-[300px] overflow-y-auto bg-gray-50 dark:bg-gray-900 rounded border p-3">
                    <div className="whitespace-pre-wrap break-words text-sm text-foreground leading-relaxed">
                      {response}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Legend note */}
            {originalRequest.metadata?._maitai_test_request_response && (
              <div className="mt-4 pt-3 border-t border-gray-100 dark:border-gray-700">
                <div className="text-xs text-muted-foreground">
                  <span className="text-maitai-green">*</span> Indicates edited/corrected response from test set
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

interface TestRunComparisonProps {
  testRunIds: number[];
  apiKey: ApiKeyOrGetter;
}

interface TestRunResult {
  chat_completion_request_test_id: number;
  original_request_id: string;
  original_request: ChatCompletionRequest;
  test_run_requests: {
    [testRunId: string]: {
      request: TestRunRequest;
      model: string;
    };
  };
}

interface TableDataItem {
  key: string;
  requestId: string;
  originalRequest: ChatCompletionRequest;
  chatCompletionRequestTestId: number;
  [testRunId: string]:
    | { request: TestRunRequest; model: string }
    | null
    | string
    | number
    | ChatCompletionRequest;
}

interface DraggableHeaderCellProps {
  index: number;
  moveColumn: (dragIndex: number, hoverIndex: number) => void;
  children: React.ReactNode;
}

const DraggableHeaderCell: React.FC<DraggableHeaderCellProps> = ({
  index,
  moveColumn,
  children,
}) => {
  const [{ isDragging }, drag] = useDrag({
    type: "DraggableColumn",
    item: { index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [, drop] = useDrop({
    accept: "DraggableColumn",
    hover(item: { index: number }) {
      if (item.index !== index) {
        moveColumn(item.index, index);
        item.index = index;
      }
    },
  });

  return (
    <div
      ref={(node) => {
        drag(node);
        drop(node);
      }}
      className={cn(
        "flex items-center justify-center cursor-move transition-opacity",
        isDragging && "opacity-50"
      )}>
      <GripVerticalIcon className="w-4 h-4 mr-2 text-muted-foreground" />
      {children}
    </div>
  );
};

const TestRunComparison: React.FC<TestRunComparisonProps> = ({
  testRunIds,
  apiKey,
}) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [comparisonData, setComparisonData] = useState<TestRunResult[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [comparisonDataByPage, setComparisonDataByPage] = useState<
    Record<string, TestRunResult[]>
  >({});
  const [columnOrder, setColumnOrder] = useState<number[]>([]);

  // Static page size
  const pageSize = 10;

  // Reset state when testRunIds change
  useEffect(() => {
    setComparisonData([]);
    setComparisonDataByPage({});
    setCurrentPage(1);
    setError(null);
    setTotalCount(0);
    setColumnOrder(testRunIds);
  }, [testRunIds]);

  // Fetch comparison data for the current page
  useEffect(() => {
    if (!testRunIds || testRunIds.length === 0) {
      return;
    }

    // Create a cache key that includes both page and size
    const cacheKey = `${currentPage}-${pageSize}`;

    if (comparisonDataByPage[cacheKey]) {
      // Use cached data
      setComparisonData(comparisonDataByPage[cacheKey]);
    } else {
      // Fetch new data
      fetchComparisonData(currentPage);
    }
  }, [currentPage, testRunIds]);

  const fetchComparisonData = (page: number) => {
    const offset = (page - 1) * pageSize;
    setLoading(true);

    // Add a small delay to ensure we can see the logs in sequence
    setTimeout(() => {
      TestRunService.getTestRunsComparison(
        testRunIds,
        offset,
        pageSize,
        setLoading,
        apiKey
      ).subscribe(
        (response: { items: TestRunResult[]; total: number }) => {
          const newData = response?.items || [];
          setComparisonData(newData);
          setTotalCount(response?.total || 0);

          // Use a cache key that includes both page and size
          const cacheKey = `${page}-${pageSize}`;

          setComparisonDataByPage((prev) => ({
            ...prev,
            [cacheKey]: newData,
          }));

          setLoading(false);
        },
        (error: Error) => {
          console.error("Error fetching test run comparison data:", error);
          setError(
            "Failed to load comparison data: " +
              (error.message || "Unknown error")
          );
          setLoading(false);
        }
      );
    }, 100);
  };

  const renderScore = (testRunRequest: TestRunRequest | null) => {
    if (
      !testRunRequest ||
      !testRunRequest.qualitative_evaluation_results ||
      !Array.isArray(testRunRequest.qualitative_evaluation_results) ||
      testRunRequest.qualitative_evaluation_results.length === 0
    ) {
      return <span className="text-sm text-muted-foreground">N/A</span>;
    }

    const results = testRunRequest.qualitative_evaluation_results;
    const maxPossibleScore = results.length * 5; // Each result can score max 5
    const totalScore = results.reduce(
      (sum, result) => sum + (result.score_value || 0),
      0
    );

    // Avoid division by zero
    const normalizedScore =
      maxPossibleScore > 0
        ? Math.round((totalScore / maxPossibleScore) * 100)
        : 0;

    const getScoreColor = (score: number) => {
      // Using the same color system as TestRunDetail.tsx score distribution
      if (score >= 90) return {
        bg: "bg-[#52c41a]", // PERFECT - green
        text: "text-white",
        border: "border-[#52c41a]"
      };
      if (score >= 75) return {
        bg: "bg-[#b7eb8f]", // GREAT - light green  
        text: "text-green-800",
        border: "border-[#b7eb8f]"
      };
      if (score >= 60) return {
        bg: "bg-[#d9d9d9]", // GOOD - gray
        text: "text-gray-800", 
        border: "border-[#d9d9d9]"
      };
      if (score >= 40) return {
        bg: "bg-[#ffa39e]", // FAIR - light red
        text: "text-red-800",
        border: "border-[#ffa39e]"
      };
      return {
        bg: "bg-[#f5222d]", // POOR - red
        text: "text-white",
        border: "border-[#f5222d]"
      };
    };

    const sortedResults = [...results].sort((a, b) => a.criteria.localeCompare(b.criteria));
    const scoreColors = getScoreColor(normalizedScore);

    return (
      <ScoreTooltip score={normalizedScore} results={sortedResults}>
        <span 
          className={cn(
            "inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium cursor-help",
            scoreColors.bg,
            scoreColors.text,
            scoreColors.border
          )}
        >
          {normalizedScore}%
        </span>
      </ScoreTooltip>
    );
  };

  // Helper to get test run responses for a record
  const getTestRunResponses = (record: TableDataItem) => {
    return columnOrder
      .map((testRunId) => {
        const testRunData = record[testRunId.toString()] as {
          request: TestRunRequest;
          model: string;
        };
        if (testRunData?.request?.response) {
          return {
            testRunId: testRunId.toString(),
            response:
              testRunData.request.response.choices?.[0]?.message?.content ||
              "No response",
            model: testRunData.model,
          };
        }
        return null;
      })
      .filter(
        (item): item is { testRunId: string; response: string; model: string } => item !== null
      );
  };

  const moveColumn = (dragIndex: number, hoverIndex: number) => {
    const dragColumn = columnOrder[dragIndex];
    setColumnOrder(
      update(columnOrder, {
        $splice: [
          [dragIndex, 1],
          [hoverIndex, 0, dragColumn],
        ],
      })
    );
  };

  // Prepare table headers and cells
  const renderHeaders = () => {
    return (
      <TableRow className="border-b border-border hover:bg-transparent">
        <TableHead className="w-[150px] font-gc-med text-muted-foreground">
          Request ID
        </TableHead>
        {columnOrder.map((testRunId, index) => {
          const model = comparisonData[0]?.test_run_requests[testRunId]?.model;
          return (
            <TableHead key={testRunId} className="w-[100px] text-center">
              <DraggableHeaderCell index={index} moveColumn={moveColumn}>
                <div className="text-center">
                  <div className="font-gc-med text-muted-foreground mb-1">Run {testRunId}</div>
                  <div className="text-xs text-muted-foreground">
                    {model || "Unknown"}
                  </div>
                </div>
              </DraggableHeaderCell>
            </TableHead>
          );
        })}
        <TableHead className="w-[120px] text-center font-gc-med text-muted-foreground">
          Actions
        </TableHead>
      </TableRow>
    );
  };

  const renderLoadingRows = () => {
    return [...Array(5)].map((_, i) => (
      <TableRow key={`loading-${i}`} className="border-b border-border/50">
        <TableCell className="py-4">
          <Skeleton className="h-8 w-16 rounded" />
        </TableCell>
        {columnOrder.map((testRunId) => (
          <TableCell key={testRunId} className="py-4 text-center">
            <Skeleton className="h-6 w-12 rounded-full mx-auto" />
          </TableCell>
        ))}
        <TableCell className="py-4 text-center">
          <Skeleton className="h-8 w-20 rounded mx-auto" />
        </TableCell>
      </TableRow>
    ));
  };

  // Prepare data for the table
  const getTableData = (): TableDataItem[] => {
    if (!comparisonData || comparisonData.length === 0) {
      return [];
    }

    return comparisonData.map((item) => {
      const rowData: TableDataItem = {
        key: item.chat_completion_request_test_id.toString(),
        requestId: item.original_request_id || "Unknown",
        originalRequest: item.original_request,
        chatCompletionRequestTestId: item.chat_completion_request_test_id,
      };

      // Add test run results to the row
      if (item.test_run_requests) {
        Object.entries(item.test_run_requests).forEach(
          ([testRunId, testRunData]) => {
            rowData[testRunId] = testRunData;
          }
        );
      }

      return rowData;
    });
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const tableData = getTableData();
  const totalPages = Math.ceil(totalCount / pageSize);

  if (error) {
    return (
      <Card className="border-border">
        <CardContent className="p-6">
          <div className="flex items-center gap-2 text-red-600">
            <AlertCircleIcon className="w-5 h-5" />
            <div>
              <div className="font-medium">Error</div>
              <div className="text-sm text-muted-foreground">{error}</div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="mt-5">
        <Card className="border-border">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  {renderHeaders()}
                </TableHeader>
                <TableBody>
                  {loading ? (
                    renderLoadingRows()
                  ) : tableData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={columnOrder.length + 2} className="py-24 text-center">
                        <div className="space-y-3">
                          <h3 className="text-lg font-gc-med text-foreground">
                            No comparison data available
                          </h3>
                          <p className="text-muted-foreground">
                            Try selecting different test runs to compare.
                          </p>
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : (
                    tableData.map((record) => (
                      <TableRow 
                        key={record.key} 
                        className="border-b border-border/50 hover:bg-accent/20 transition-colors"
                      >
                        <TableCell className="py-4">
                          {record.requestId ? (
                            <RequestViewButton
                              apiKey={apiKey}
                              request={record.originalRequest}
                              systemPrompt={
                                record.originalRequest?.params?.messages?.find(
                                  (m: ChatMessage) => m.role === "system"
                                )?.content || ""
                              }>
                              <code className="text-sm font-mono bg-muted px-2 py-1 rounded text-foreground">
                                {record.requestId.length > 4 ? `...${record.requestId.slice(-4)}` : record.requestId}
                              </code>
                            </RequestViewButton>
                          ) : (
                            <span className="text-sm text-muted-foreground">Unknown</span>
                          )}
                        </TableCell>
                        {columnOrder.map((testRunId) => {
                          const testRunData = record[testRunId.toString()] as {
                            request: TestRunRequest;
                            model: string;
                          } | null;
                          return (
                            <TableCell key={testRunId} className="py-4 text-center">
                              {renderScore(testRunData?.request || null)}
                            </TableCell>
                          );
                        })}
                        <TableCell className="py-4 text-center">
                          <ResponsesTooltip 
                            originalRequest={record.originalRequest}
                            testRunResponses={getTestRunResponses(record)}
                          >
                            <Button
                              variant="ghost"
                              size="sm"
                              className="gap-1"
                              onClick={(e) => e.stopPropagation()}>
                              <DiffIcon className="w-3 h-3" />
                              Responses
                            </Button>
                          </ResponsesTooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-6 py-4 border-t border-border">
                <div className="text-sm text-muted-foreground">
                  Showing {(currentPage - 1) * pageSize + 1} to {Math.min(currentPage * pageSize, totalCount)} of {totalCount} items
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="gap-1"
                  >
                    Previous
                  </Button>
                  <div className="flex items-center gap-1">
                    {[...Array(totalPages)].map((_, i) => {
                      const pageNum = i + 1;
                      const isCurrentPage = pageNum === currentPage;
                      return (
                        <Button
                          key={pageNum}
                          variant={isCurrentPage ? "default" : "ghost"}
                          size="sm"
                          onClick={() => handlePageChange(pageNum)}
                          className={cn(
                            "w-8 h-8 p-0",
                            isCurrentPage && "bg-maitai-green text-white hover:bg-maitai-green/90"
                          )}
                        >
                          {pageNum}
                        </Button>
                      );
                    })}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="gap-1"
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DndProvider>
  );
};

export default TestRunComparison;
